import { ReactElement, useState } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { ActionTabs } from '@backrs_ui/common-ui/src/atoms/ActionTabs';
import { AppIcon, AppIcons, ICON_SIZES } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import {
  BASE_NUGGET_HEIGHT,
  BASE_NUGGET_WIDTH,
} from '@backrs_ui/common-ui/src/molecules/Nugget/BaseNugget';

import { Box, Typography, SxProps, Theme } from '@mui/material';

const EDUCATIONAL_INVESTMENT_STRINGS = appStrings.modules.educationalInvestment;
const ACTION_TAB_STRINGS = appStrings.modules.actionTabs;

type EducationalInvestmentCardProps = Readonly<{
  variant:
    | {
        type: 'SMALL';
        title: string;
        icon: AppIcons;
        description: string;
        toSucceedItems: readonly string[];
        hasBookmarked?: boolean;
        onClick?: () => void;
      }
    | {
        type: 'LARGE';
        title: string;
        icon: AppIcons;
        description: string;
        toSucceedItems: readonly string[];
        hasBookmarked?: boolean;
        onAddTask: () => void;
        onShare: () => void;
        onBookmark: () => void;
        onClick?: () => void;
        hideActions?: boolean;
      };
}>;

export const EducationalInvestmentCard = ({
  variant,
}: EducationalInvestmentCardProps): ReactElement => {
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  return (
    <Box sx={cardContainerSx(variant.type)} onClick={variant.onClick}>
      <Box sx={headerSx}>
        <Box sx={iconRowSx}>
          <Box sx={iconSx}>
            <AppIcon iconName={variant.icon} diameter="20px" color="Colors/Base/White" />
          </Box>
          {variant.type === 'LARGE' && !variant.hideActions && (
            <ActionTabs
              actions={[
                {
                  icon: variant.hasBookmarked ? 'BookmarkCheck' : 'Bookmark',
                  onClick: variant.onBookmark,
                  tooltipHoverText: ACTION_TAB_STRINGS.bookmark,
                },
                {
                  icon: 'Share06',
                  onClick: variant.onShare,
                  tooltipHoverText: ACTION_TAB_STRINGS.share,
                },
                {
                  icon: 'BookmarkAdd',
                  onClick: variant.onAddTask,
                  tooltipHoverText: ACTION_TAB_STRINGS.task,
                },
              ]}
              showCopySuccess={showCopySuccess}
              onCopySuccessChange={setShowCopySuccess}
              borderColor="Colors/Border/border-primary"
            />
          )}
        </Box>
        <Box sx={titleAndDescriptionSx}>
          <Typography
            variant="text-sm-medium"
            color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
          >
            {EDUCATIONAL_INVESTMENT_STRINGS.educationalInvestment}
          </Typography>
          <Typography
            variant="display-xs-semibold"
            color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
          >
            {variant.title}
          </Typography>
          <Typography variant="text-sm-regular">{variant.description}</Typography>
        </Box>
      </Box>
      {!!variant.toSucceedItems?.length && (
        <Box sx={toSucceedItemsSx(variant.type)}>
          <Typography variant="text-sm-semibold">
            {EDUCATIONAL_INVESTMENT_STRINGS.toSucceed}
          </Typography>
          {variant.toSucceedItems.map((item) => (
            <Box key={item} sx={toSucceedItemSx(variant.type)}>
              <Box sx={toSucceedItemIconSx}>
                <AppIcon iconName="CheckCircleBroken" diameter="16px" color="Brand/600" />
              </Box>
              <Typography variant="text-sm-regular" sx={toSucceedItemTextSx(variant.type)}>
                {item}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const toSucceedItemSx =
  (variant: 'SMALL' | 'LARGE'): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: backrs.spacing['spacing-sm'],
    ...(variant === 'SMALL' && {
      width: '100%',
    }),
  });

const toSucceedItemIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: ICON_SIZES['16px'],
  height: ICON_SIZES['16px'],
  mt: backrs.spacing['spacing-xxs'],
});

const toSucceedItemTextSx =
  (variant: 'SMALL' | 'LARGE'): SxProps<Theme> =>
  ({ backrs }) => ({
    color: backrs.colors['Colors/Text/text-primary'],
    ...(variant === 'SMALL' && {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      flex: 1,
      minWidth: 0,
    }),
  });

const cardContainerSx =
  (variant: 'SMALL' | 'LARGE'): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    padding: backrs.spacing['spacing-2xl'],
    flexDirection: 'column',
    borderRadius: variant === 'SMALL' ? backrs.radius['radius-4xl'] : backrs.radius['radius-xl'],
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    overflow: 'hidden',
    height: variant === 'SMALL' ? BASE_NUGGET_HEIGHT : undefined,
    maxWidth: variant === 'SMALL' ? BASE_NUGGET_WIDTH : undefined,
    backgroundColor: backrs.colors['Colors/Base/White'],
    cursor: variant === 'SMALL' ? 'pointer' : undefined,
  });

const iconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: backrs.colors['Brand/500'],
  width: ICON_SIZES['40px'],
  height: ICON_SIZES['40px'],
  minWidth: ICON_SIZES['40px'],
  minHeight: ICON_SIZES['40px'],
  borderRadius: backrs.radius['radius-md'],
  border: `2px solid ${backrs.colors['Colors/Foreground/fg-quaternary-hover']}`,
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  position: 'relative',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const iconRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  position: 'relative',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-sm'],
  marginBottom: backrs.spacing['spacing-lg'],
});

const toSucceedItemsSx =
  (variant: 'SMALL' | 'LARGE'): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: variant === 'SMALL' ? backrs.spacing['spacing-sm'] : backrs.spacing['spacing-md'],
    paddingTop: variant === 'SMALL' ? backrs.spacing['spacing-sm'] : backrs.spacing['spacing-2xl'],
  });

const titleAndDescriptionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
  gap: backrs.spacing['spacing-xs'],
});
